import { useForm, zodResolver } from '@mantine/form';
import { mergeWith } from 'lodash';

import { TSDData } from '@e-origin/contracts';

import { useUpdateTSD } from '@hooks/tsd';
import { TSDViewGeneralInfoFormValues, tsdViewGeneralInfoSchema } from './validateTSDViewGeneralInfoForm';

interface UseTsdViewGeneralInfoFormInitializerProps {
  tsd: TSDData;
}

export const useTsdViewGeneralInfoFormInitializer = ({ tsd }: UseTsdViewGeneralInfoFormInitializerProps) => {
  const updateTsdMutation = useUpdateTSD();

  const buildFormValues = (t: TSDData): TSDViewGeneralInfoFormValues => ({
    customer: t.customer ?? '',

    generalInfo: {
      ensReUseIndicator: t.generalInfo?.ensReUseIndicator ?? false,
      declarationDate: t.generalInfo?.declarationDate ? new Date(t.generalInfo.declarationDate) : null,
    },

    supervisingCustomsOffice: {
      referenceNumber: t.supervisingCustomsOffice?.referenceNumber ?? '',
    },

    representative: {
      identificationNumber: t.representative?.identificationNumber ?? '',
      name: t.representative?.name ?? '',
      status: t.representative?.status ?? '',
      communication: {
        phoneNumber: t.representative?.communication?.phoneNumber ?? '',
        eMailAddress: t.representative?.communication?.eMailAddress ?? '',
      },
    },

    locationOfGoods: {
      typeOfLocation: t.locationOfGoods?.typeOfLocation ?? '',
      qualifierOfIdentification: t.locationOfGoods?.qualifierOfIdentification ?? '',
      authorisationNumber: t.locationOfGoods?.authorisationNumber ?? '',
      additionalIdentifier: t.locationOfGoods?.additionalIdentifier ?? '',
      UNLocode: t.locationOfGoods?.UNLocode ?? '',
      customsOffice: {
        referenceNumber: t.locationOfGoods?.customsOffice?.referenceNumber ?? '',
      },
      gnss: {
        latitude: t.locationOfGoods?.gnss?.latitude ?? '',
        longitude: t.locationOfGoods?.gnss?.longitude ?? '',
      },
      economicOperator: {
        identificationNumber: t.locationOfGoods?.economicOperator?.identificationNumber ?? '',
      },
      address: {
        streetAndNumber: t.locationOfGoods?.address?.streetAndNumber ?? '',
        country: t.locationOfGoods?.address?.country ?? '',
        postcode: t.locationOfGoods?.address?.postcode ?? '',
        city: t.locationOfGoods?.address?.city ?? '',
      },
    },

    arrivalTransportMeans: {
      identificationNumber: t.arrivalTransportMeans?.identificationNumber ?? '',
      typeOfIdentification: t.arrivalTransportMeans?.typeOfIdentification ?? '',
      nationality: t.arrivalTransportMeans?.nationality ?? '',
      modeOfTransportAtTheBorder: t.arrivalTransportMeans?.modeOfTransportAtTheBorder ?? '',
    },

    warehouse: {
      type: t.warehouse?.type ?? '',
      identifier: t.warehouse?.identifier ?? '',
    },

    placeOfUnloading: {
      UNLocode: t.placeOfUnloading?.UNLocode ?? '',
    },

    personNotifyingTheArrivalAfterMovement: {
      identificationNumber: t.personNotifyingTheArrivalAfterMovement?.identificationNumber ?? '',
    },

    declarantPerson: {
      identificationNumber: t.declarantPerson?.identificationNumber ?? '',
      name: t.declarantPerson?.name ?? '',
      communication: {
        phoneNumber: t.declarantPerson?.communication?.phoneNumber ?? '',
        eMailAddress: t.declarantPerson?.communication?.eMailAddress ?? '',
      },
    },

    refreshCustomerData: false,
  });

  const form = useForm<TSDViewGeneralInfoFormValues>({
    initialValues: buildFormValues(tsd),
    validate: zodResolver(tsdViewGeneralInfoSchema),
    validateInputOnBlur: true,
  });

  const onSubmit = async (values: TSDViewGeneralInfoFormValues) => {
    const { refreshCustomerData } = values;

    delete values.refreshCustomerData;

    updateTsdMutation.mutate({
      tsd: mergeWith({ _id: tsd._id, name: tsd.name }, values, (objValue, srcValue) => (Array.isArray(srcValue) ? srcValue : undefined)),
      refreshCustomerData,
    });
    form.setFieldValue('refreshCustomerData', false);
  };

  return { form, onSubmit, buildFormValues };
};
